import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, Play, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { eventsApi } from '../api/events';
import { waitlistApi } from '../api/waitlist';
import { Event, WaitlistStatus } from '../types';
import { EventCard } from '../components/Events/EventCard';
import { CreateEventModal } from '../components/Events/CreateEventModal';
import { Button } from '../components/UI/Button';
import { TabBar, TabItem } from '../components/UI/TabBar';
import { EventCardSkeleton, Skeleton } from '../components/UI/Skeleton';
import { PageHeader } from '../components/UI/PageHeader';
import { MachhaCharacter } from '../lib/machhaAsset';
import { track, EVENTS } from '../lib/mixpanel';
import { useAuthStore } from '../store/authStore';
import { clayStaggerContainer, clayStaggerItem } from '../constants/animations';
import { usePageTitle } from '../hooks/usePageTitle';
import styles from './EventsPage.module.css';

const AUTO_RETRY_SECS = 5;

function EventsGateContent() {
  const navigate = useNavigate();
  const [waitlistStatus, setWaitlistStatus] = useState<WaitlistStatus | null | 'loading'>('loading');

  useEffect(() => {
    waitlistApi
      .getMe()
      .then((entry) => setWaitlistStatus(entry.status))
      .catch(() => setWaitlistStatus(null));
  }, []);

  if (waitlistStatus === 'loading') {
    return (
      <div className={styles['page-container']}>
        <div className={styles['loading-container']}>
          <div className={styles['spinner']} />
        </div>
      </div>
    );
  }

  if (waitlistStatus === 'pending') {
    return (
      <div className={styles['page-container']}>
        <motion.div
          className={styles['gate-container']}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 28 }}
        >
          <MachhaCharacter state="Loading" size={160} />
          <h2 className={styles['gate-title']}>
            Access request under review
          </h2>
          <p className={styles['gate-description']}>
            We've received your request to join the private beta. You'll be notified once it's reviewed.
          </p>
        </motion.div>
      </div>
    );
  }

  if (waitlistStatus === 'rejected') {
    return (
      <div className={styles['page-container']}>
        <motion.div
          className={styles['gate-container']}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 28 }}
        >
          <MachhaCharacter state="Failure" size={160} />
          <h2 className={styles['gate-title']}>
            Access request not approved
          </h2>
          <p className={styles['gate-description']}>
            Unfortunately your request wasn't approved at this time.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={styles['page-container']}>
      <motion.div
        className={styles['gate-container']}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
      >
        <MachhaCharacter state="Forbidden" size={160} />
        <h2 className={styles['gate-title']}>
          Private beta required
        </h2>
        <p className={styles['gate-description']}>
          Creating and managing events is currently invite-only. Request access to join the private beta.
        </p>
        <div className={styles['gate-cta']}>
          <Button onClick={() => navigate('/waitlist/onboarding')}>
            Request Access
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

const EventsTabContent: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [countdown, setCountdown] = useState(AUTO_RETRY_SECS);
  const [showCreate, setShowCreate] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeTab, setActiveTab] = useState<'live' | 'ended'>('live');

  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoRetryRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    loadEvents(1);
    return clearTimers;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const refresh = () => loadEvents(1);
    window.addEventListener('pm:events-refresh', refresh);
    return () => window.removeEventListener('pm:events-refresh', refresh);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function clearTimers() {
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (autoRetryRef.current) clearTimeout(autoRetryRef.current);
  }

  function startAutoRetry() {
    setCountdown(AUTO_RETRY_SECS);
    countdownRef.current = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) { clearInterval(countdownRef.current!); return 0; }
        return c - 1;
      });
    }, 1000);
    autoRetryRef.current = setTimeout(() => handleRetry(), AUTO_RETRY_SECS * 1000);
  }

  const loadEvents = async (pageNum: number) => {
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);
    setHasError(false);

    try {
      const [data] = await Promise.all([
        eventsApi.list(pageNum),
        pageNum === 1 ? new Promise(r => setTimeout(r, 600)) : Promise.resolve(),
      ]);
      if (pageNum === 1) {
        setEvents(data.events);
      } else {
        setEvents(prev => [...prev, ...data.events]);
      }
      setPage(pageNum);
      setTotalPages(data.pagination.pages);
      if (pageNum === 1) {
        track(EVENTS.EVENTS_LIST_VIEWED, {
          event_count: data.events.length,
          total_pages: data.pagination.pages,
        });
      }
    } catch {
      if (pageNum === 1) {
        setHasError(true);
        startAutoRetry();
      } else {
        toast.error('Failed to load more events');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setRetrying(false);
    }
  };

  function handleRetry() {
    clearTimers();
    setRetrying(true);
    loadEvents(1);
  }

  const handleCreated = (event: Event) => {
    setEvents(prev => [event, ...prev]);
    toast.success(`Event "${event.name}" created!`);
  };

  return (
    <div className={styles['page-container']}>

      <PageHeader
        title="Events"
        subtitle="Create and manage events to organize your photography"
        className={styles['header-spacing']}
        right={
          <Button onClick={() => setShowCreate(true)}>
            <svg className={styles['icon-sm']} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Event
          </Button>
        }
      />

      {loading ? (
        /* ── Loading state ─────────────────────────────────────────────── */
        <div>
          {/* tabs shimmer — mirrors the underline TabBar shown once loaded */}
          <div className={styles['tabs-wrap']}>
            <div className={styles['tabs-skeleton-bar']} aria-hidden>
              {[0, 1].map(i => (
                <div key={i} className={styles['tabs-skeleton-tab']}>
                  <Skeleton className={styles['tabs-skeleton-icon']} />
                  <Skeleton className={styles['tabs-skeleton-label']} />
                </div>
              ))}
            </div>
          </div>
          <motion.div
            className={styles['grid']}
            variants={clayStaggerContainer}
            initial="hidden"
            animate="visible"
          >
            {[0, 1, 2].map(i => (
              <motion.div key={i} variants={clayStaggerItem}>
                <EventCardSkeleton />
              </motion.div>
            ))}
          </motion.div>
          <motion.p
            className={styles['loading-hint']}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Loading your events…
          </motion.p>
        </div>

      ) : hasError ? (
        /* ── Error state ───────────────────────────────────────────────── */
        <motion.div
          className={styles['state-container']}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 28 }}
        >
          <MachhaCharacter state="Error" size={160} />
          <h2 className={styles['state-title']}>
            Couldn't load your events
          </h2>
          <p className={styles['state-description']}>
            We're having a temporary issue fetching your events. Your data is safe — this usually resolves on its own.
          </p>
          <div className={styles['cta-spacing']}>
            <Button onClick={handleRetry} loading={retrying}>
              <RefreshCw className={styles['icon-sm']} />
              Retry now
            </Button>
          </div>
          <AnimatePresence>
            {!retrying && countdown > 0 && (
              <motion.p
                className={styles['countdown-text']}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                Auto-retrying in {countdown}s…
              </motion.p>
            )}
          </AnimatePresence>
        </motion.div>

      ) : events.length === 0 ? (
        /* ── Empty state ───────────────────────────────────────────────── */
        <motion.div
          className={styles['state-container']}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 28 }}
        >
          <MachhaCharacter state="Onboarding" size={180} />
          <h2 className={styles['state-title-lg']}>
            No events yet
          </h2>
          <p className={styles['state-description-wide']}>
            Create your first photography event to organize shoots, manage FTP uploads, and export photos to your team — all in one place.
          </p>
          <div className={styles['cta-spacing-lg']}>
            <Button onClick={() => setShowCreate(true)}>
              <svg className={styles['icon-sm']} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create your first event
            </Button>
          </div>
        </motion.div>

      ) : (
        /* ── List state ────────────────────────────────────────────────── */
        <>
          {(() => {
            const liveEvents = events.filter(e => e.status === 'active');
            const endedEvents = events.filter(e => e.status === 'ended');
            const tabs: TabItem<'live' | 'ended'>[] = [
              { id: 'live' as const, label: `Live${liveEvents.length > 0 ? ` (${liveEvents.length})` : ''}`, icon: Play },
              { id: 'ended' as const, label: `Ended${endedEvents.length > 0 ? ` (${endedEvents.length})` : ''}`, icon: CheckCircle },
            ];

            const displayEvents = activeTab === 'live' ? liveEvents : endedEvents;

            return (
              <>
                <div className={styles['tabs-wrap']}>
                  <TabBar
                    tabs={tabs}
                    activeTab={activeTab}
                    onChange={setActiveTab}
                    layoutId="events-tab"
                    variant="underline"
                  />
                </div>

                <AnimatePresence mode="wait">
                  {displayEvents.length === 0 ? (
                    <motion.div
                      key={`empty-${activeTab}`}
                      className={styles['empty-tab-state']}
                      initial={{ opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 28 }}
                    >
                      <motion.div
                        animate={{ y: [0, -6, 0] }}
                        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                      >
                        <MachhaCharacter state={activeTab === 'live' ? 'Empty' : 'Milestone'} size={160} />
                      </motion.div>
                      <h3 className={styles['empty-tab-title']}>
                        {activeTab === 'live' ? 'Nothing live right now' : 'No ended events yet'}
                      </h3>
                      <p className={styles['empty-tab-description']}>
                        {activeTab === 'live'
                          ? 'Start a new event and go live — your crew is waiting.'
                          : 'Events you wrap up will show here for reference.'}
                      </p>
                      {activeTab === 'live' && (
                        <div className={styles['empty-tab-cta']}>
                          <Button onClick={() => setShowCreate(true)}>
                            <svg className={styles['icon-sm']} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Create an event
                          </Button>
                        </div>
                      )}
                    </motion.div>
                  ) : (
                    <motion.div
                      key={`grid-${activeTab}`}
                      className={styles['grid']}
                      variants={clayStaggerContainer}
                      initial="hidden"
                      animate="visible"
                      exit={{ opacity: 0 }}
                    >
                      {displayEvents.map(event => (
                        <EventCard key={event._id} event={event} />
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                <AnimatePresence>
                  {page < totalPages && (
                    <motion.div
                      className={styles['load-more-container']}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <Button
                        variant="secondary"
                        loading={loadingMore}
                        onClick={() => loadEvents(page + 1)}
                      >
                        Load More
                      </Button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            );
          })()}
        </>
      )}

      <CreateEventModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={handleCreated}
      />
    </div>
  );
};

export function EventsPage() {
  usePageTitle('Events');
  const { user } = useAuthStore();
  const showEventsContent = user?.betaApproved || user?.isOwner;

  return showEventsContent ? <EventsTabContent /> : <EventsGateContent />;
}
